<script setup lang="ts">
/**
 * People app preview, laid out like the players (player-web render/people.ts, Android PeopleView.kt): spotlight,
 * team grid, birthdays or directory, a page at a time. Thumbnails (`live` off) hold the first page.
 */
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { directoryGrid, localDate, peopleGrid, peopleLabels, peopleList, peoplePerPage, type DataFeed, type PeopleWidget, type Person } from '@codot/contracts';
import { loadFeed } from './ChartPreview.vue';

const props = withDefaults(defineProps<{ config: PeopleWidget; zone?: string; live?: boolean }>(), { zone: 'Asia/Dubai', live: true });
const { t } = useI18n();
const feed = shallowRef<DataFeed | null>(null);
const failed = ref(false);
watch(
  () => props.config.feedId,
  async (id) => {
    feed.value = null;
    failed.value = false;
    if (!id) return;
    try {
      feed.value = await loadFeed(id);
    } catch {
      failed.value = true;
    }
  },
  { immediate: true },
);
const people = computed<Person[]>(() => (feed.value?.table ? peopleList(feed.value.table, props.config, localDate(Date.now(), props.zone)) : []));
const L = computed(() => peopleLabels(props.config.language));
const per = computed(() => peoplePerPage(props.config));
const pages = computed(() => Math.max(1, Math.ceil(people.value.length / per.value)));
const page = ref(0);
let timer = 0;
watch(
  () => [props.live, props.config.slideSeconds, pages.value] as const,
  ([live, secs, n]) => {
    clearInterval(timer);
    page.value = 0;
    if (live && n > 1) timer = window.setInterval(() => (page.value = (page.value + 1) % pages.value), secs * 1000);
  },
  { immediate: true },
);
onBeforeUnmount(() => clearInterval(timer));
const slice = computed(() => people.value.slice(page.value * per.value, page.value * per.value + per.value));
const title = computed(() => props.config.title || (props.config.style === 'birthdays' ? L.value.birthdaysTitle : ''));
const ar = computed(() => props.config.language === 'ar');
const fmt = computed(() => new Intl.DateTimeFormat(ar.value ? 'ar-AE' : 'en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }));
const initials = (p: Person) => p.name.split(/\s+/).map((w) => w[0] ?? '').slice(0, 2).join('').toUpperCase();
const sub = (p: Person) => (props.config.style === 'birthdays' && p.month ? (p.inDays === 0 ? L.value.today : fmt.value.format(Date.UTC(2000, p.month - 1, p.day!))) : p.role);
const grid = computed(() => peopleGrid(props.config, slice.value.length));
const cellH = computed(() => `min(${(78 / grid.value.rows).toFixed(1)}cqh, ${(88 / grid.value.cols).toFixed(1)}cqw)`);
const dirGrid = computed(() => directoryGrid(props.config, slice.value.length));
const dirCols = computed(() => dirGrid.value.cols);
const dirRows = computed(() => dirGrid.value.rows);
/** Photos that failed to load fall back to initials. */
const broken = ref(new Set<string>());
const message = computed(() =>
  !props.config.feedId ? t('appsCat.pePickFeed') : failed.value ? t('appsCat.chartNoFeed') : feed.value && !feed.value.table ? t('appsCat.chartNoTable') : props.config.style === 'birthdays' ? L.value.none : '–',
);
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden [container-type:size]" :dir="ar ? 'rtl' : 'ltr'" :style="{ background: config.background, color: config.foreground, padding: 'min(4cqh, 4cqw)' }">
    <p v-if="title" class="shrink-0 truncate font-bold" style="font-size: min(8cqh, 5.5cqw); margin-bottom: min(3cqh, 3cqw)">{{ title }}</p>
    <div class="relative min-h-0 flex-1">
      <p v-if="!people.length" class="absolute inset-0 flex items-center justify-center text-center opacity-70" style="font-size: min(7cqh, 5cqw)">{{ message }}</p>
      <Transition v-else name="pe">
        <!-- Spotlight -->
        <div v-if="config.style === 'spotlight'" :key="page" class="absolute inset-0 flex items-center" style="gap: min(6cqh, 5cqw)">
          <div class="flex shrink-0 items-center justify-center overflow-hidden" :style="{ width: 'min(78cqh, 42cqw)', height: 'min(78cqh, 42cqw)', borderRadius: 'min(2cqh, 2cqw)', background: config.accent }">
            <img v-if="slice[0]!.photo && !broken.has(slice[0]!.photo)" :src="slice[0]!.photo" alt="" class="h-full w-full object-cover" @error="broken.add(slice[0]!.photo!)" />
            <span v-else class="font-bold text-[#111]" style="font-size: min(28cqh, 15cqw)">{{ initials(slice[0]!) }}</span>
          </div>
          <div class="min-w-0 flex-1">
            <p class="font-bold" style="font-size: min(11cqh, 6.5cqw); line-height: 1.1">{{ slice[0]!.name }}</p>
            <p v-if="slice[0]!.role" class="font-medium" :style="{ color: config.accent, fontSize: 'min(6.5cqh, 4cqw)', marginTop: '1.5cqh' }">{{ slice[0]!.role }}</p>
            <p v-if="slice[0]!.detail" class="opacity-85" style="font-size: min(5.5cqh, 3.4cqw); margin-top: 4cqh; line-height: 1.35">“{{ slice[0]!.detail }}”</p>
          </div>
        </div>
        <!-- Directory -->
        <div
          v-else-if="config.style === 'directory'"
          :key="`d${page}`"
          class="absolute inset-0 grid"
          :style="{ gridTemplateColumns: `repeat(${dirCols}, 1fr)`, gridAutoFlow: 'column', gridTemplateRows: `repeat(${dirRows}, 1fr)`, columnGap: 'min(5cqh, 4cqw)' }"
        >
          <div v-for="(p, i) in slice" :key="i" class="flex min-w-0 items-baseline border-b border-gray-500/25" :style="{ gap: '0.8em', fontSize: `min(${(55 / dirRows).toFixed(2)}cqh, 2.6cqw)` }">
            <span class="max-w-[45%] shrink-0 truncate font-bold">{{ p.name }}</span>
            <span class="min-w-0 flex-1 truncate opacity-75">{{ [p.role, p.detail].filter(Boolean).join(' · ') }}</span>
            <span v-if="p.extra" class="shrink-0 font-bold tabular-nums" :style="{ color: config.accent }">{{ p.extra }}</span>
          </div>
        </div>
        <!-- Team / birthdays -->
        <div v-else :key="`g${page}`" class="absolute inset-0 grid" :style="{ gridTemplateColumns: `repeat(${grid.cols}, 1fr)`, gridTemplateRows: `repeat(${grid.rows}, 1fr)`, gap: 'min(3cqh, 2.5cqw)' }">
          <div v-for="(p, i) in slice" :key="i" class="flex min-w-0 flex-col items-center justify-center text-center">
            <div class="flex shrink-0 items-center justify-center overflow-hidden rounded-full" :style="{ width: `calc(${cellH} * 0.6)`, height: `calc(${cellH} * 0.6)`, background: config.accent }">
              <img v-if="p.photo && !broken.has(p.photo)" :src="p.photo" alt="" class="h-full w-full object-cover" @error="broken.add(p.photo!)" />
              <span v-else class="font-bold text-[#111]" :style="{ fontSize: `calc(${cellH} * 0.21)` }">{{ initials(p) }}</span>
            </div>
            <p class="max-w-full truncate font-bold" :style="{ fontSize: `calc(${cellH} * 0.11)`, marginTop: `calc(${cellH} * 0.05)` }">{{ p.name }}</p>
            <p
              v-if="sub(p)"
              class="max-w-full truncate"
              :style="{ fontSize: `calc(${cellH} * 0.085)`, color: config.style === 'birthdays' && p.inDays === 0 ? config.accent : undefined, opacity: config.style === 'birthdays' && p.inDays === 0 ? 1 : 0.75 }"
            >
              {{ sub(p) }}
            </p>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.pe-enter-active,
.pe-leave-active {
  transition: opacity 0.7s ease;
}
.pe-enter-from,
.pe-leave-to {
  opacity: 0;
}
</style>
